/**
 * resetAndOnboarding.ts
 * リセット・オンボーディング関連メソッド
 * App.vue から切り出し
 */

import spreadsheetSyncManager from '@/js/spreadsheetSyncManager'
import { getStep } from '@/utils/onboardingSteps'

type VM = any

export function checkAndStartOnboarding(vm: VM) {
  // localStorageでオンボーディング完了フラグをチェック
  try {
    const completed = localStorage.getItem('oh3_onboarding_completed')
    // 開発モード: URLに ?debug_onboarding があれば常に表示
    const isDevelopment = window.location.search.includes('debug_onboarding')

    if (!completed || isDevelopment) {
      // 初回訪問の場合、少し遅延してから使用目的選択を表示
      setTimeout(() => {
        vm.showPurposeSelector = true
        vm.$store.commit('START_ONBOARDING')
      }, 1000)
    }
  } catch (e) {
    console.warn('localStorage not available:', e)
  }
}
// 開発用：オンボーディングをリセット
export function resetOnboarding(vm: VM) {
  try {
    localStorage.removeItem('oh3_onboarding_completed')
    vm.showPurposeSelector = false
    vm.showOnboardingTooltip = false
    vm.onboardingPurpose = null
    vm.onboardingStepIndex = 0
  } catch (e) {
    console.warn('localStorage not available:', e)
  }
}
// リセット（確実に新バージョンへ更新）
//
// 【注意】2025-11-28 キャッシュ削除処理を一時的に無効化
// 以前はここでService Workerのキャッシュを削除していたが、
// 本番環境で処理が遅い問題があり、一時的にシンプルなリロードのみに変更。
// キャッシュ削除が必要になった場合は、以下のコメントアウトされた
// resetFull() メソッドを参考に復活させること。
// Service Workerはバージョン管理(CACHE_VERSION)されており、
// 新バージョンデプロイ時に古いキャッシュは自動削除される。
//
export async function reset(vm: VM) {
  const resetStartTime = performance.now()

  // スプレッドシート同期を解除
  spreadsheetSyncManager.stopSync()
  spreadsheetSyncManager.clearMap()
  spreadsheetSyncManager.clearConfig()

  // マーカーが存在する場合は3択ダイアログを表示
  const savedMarkers = vm.$store.state.savedMarkers || []
  if (savedMarkers.length > 0) {
    const s = vm.$store.state
    s.confirmProps = {
      title: 'マーカーの確認',
      okText: '削除してリロード',
      middleText: '残してリロード',
      middleColor: 'primary',
      cancelText: 'キャンセル',
      persistent: true,
      width: 440,
      color: 'warning',
      icon: 'mdi-map-marker-question',
      iconColor: 'warning',
    }
    s.confirmMessage = `保存されたマーカーが<strong>${savedMarkers.length}件</strong>あります。`

    const choice = await new Promise(resolve => {
      s.confirmResolve = resolve
      s.showConfirm = true
    })

    // キャンセル → 何もしない
    if (choice === false) return

    // 「削除してリロード」(true) の場合のみマーカーを削除
    if (choice === true) {
      // 1. マップ上のマーカーDOM要素を削除
      const map = vm.$store.state.map01
      if (map) {
        const container = map.getContainer()
        const markerElements = container.querySelectorAll('.maplibregl-marker')
        markerElements.forEach((el: Element) => el.remove())
      }
      // 2. ストアのマーカーデータをクリアしてサーバーにも反映
      vm.$store.commit('SET_SAVED_MARKERS', [])
      try {
        await vm.$store.dispatch('saveSavedMarkers')
      } catch (e) {
        console.warn('[OH3] Failed to save empty markers to server:', e)
      }
    }
    // 「残してリロード」('middle') の場合はマーカーそのまま → 以降のリロード処理へ続行
  }

  // キャッシュ削除してからリロード（ハード再読み込み相当）

  // Service Workerキャッシュを削除（オフラインエリアは保護）
  if ('caches' in window) {
    const cacheNames = await caches.keys()
    await Promise.all(
      cacheNames
        .filter(name => !name.startsWith('offline-area-'))
        .map(name => caches.delete(name))
    )
  }

  // Service Workerの登録解除
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(r => r.unregister()))
  }


  // 授業モード中の場合はsessionStorageをクリア（リロード後に授業リスナーが再遷移する）
  const lessonMode = vm.$store.state.lessonMode
  if (lessonMode?.active && lessonMode?.classId) {
    sessionStorage.removeItem(`lesson_${lessonMode.classId}`)
  }

  // パラメータなしのURLでリロード（授業モードの場合はリスナーが再遷移する）
  const baseUrl = `${window.location.origin}${window.location.pathname}`
  window.location.replace(baseUrl)
}
// リセット（初期時に戻す）- 旧版、問題があったためコメントアウト
// async resetFull () {
//   try {
//
//     // 1. Service Workerキャッシュを削除（オフライン起動用キャッシュは保護）
//     if ('caches' in window) {
//       const cacheNames = await caches.keys()
//       await Promise.all(cacheNames.map(name => {
//         // オフラインエリアキャッシュは保護
//         if (name.startsWith('offline-area-')) {
//           return Promise.resolve()
//         }
//         // workbox-precacheは保護（オフライン起動に必須）
//         if (name.startsWith('workbox-precache')) {
//           return Promise.resolve()
//         }
//         return caches.delete(name)
//       }))
//     }
//
//     // 2. localStorage/sessionStorage をクリア（保持すべき設定以外）
//     // ユーザー設定は残すため、特定のキャッシュキーのみクリア
//     const keysToRemove = []
//     for (let i = 0; i < localStorage.length; i++) {
//       const key = localStorage.key(i)
//       // skipフラグは保持する（無限ループ防止のため）
//       // if (key && (key.startsWith('fw:') || key.includes('cache') || key.includes('version')) && key !== 'oh3_skip_next_version_check') {
//       //   keysToRemove.push(key)
//       // }
//       if (key && (key.includes('cache') || key.includes('version')) && key !== 'oh3_skip_next_version_check') {
//         keysToRemove.push(key)
//       }
//     }
//     keysToRemove.forEach(key => localStorage.removeItem(key))
//
//
//     // Chrome iOSの無限ループ防止: リセット直後であることをマーク
//     try {
//       localStorage.setItem('oh3_skip_next_version_check', Date.now().toString())
//     } catch (e) {
//       console.warn('[OH3] Failed to set skip flag:', e)
//     }
//
//     // 3. タイムスタンプを追加してキャッシュバスティング & 強制リロード
//     // iOS Safari対策: location.replaceを使用してキャッシュを完全にバイパス
//     const baseUrl = `${window.location.origin}${window.location.pathname}`
//     const timestamp = new Date().getTime()
//     // ブラウザモード無限ループ防止: _just_updatedフラグを追加
//     const urlWithTimestamp = `${baseUrl}?_nocache=${timestamp}&_just_updated=1`
//
//
//     // iOS対策: replaceを使ってキャッシュを回避
//     // Service Workerはアンレジスターしない（2回リロードを防ぐため）
//     window.location.replace(urlWithTimestamp)
//   } catch (error) {
//     console.error('[OH3] Error during reset:', error)
//     alert('リセット中にエラーが発生しました。詳細: ' + error.message)
//   }
// },
// 使用目的を選択
export function handlePurposeSelect(vm: VM, purpose: string) {
  vm.onboardingPurpose = purpose
  vm.onboardingStepIndex = 0
  vm.showPurposeSelector = false

  // TODO: レイヤーウィンドウとツールチップの自動表示（一時的に無効化）
  // レイヤーウィンドウを自動的に開く
  /* vm.$nextTick(() => {
    vm.$store.commit('setFloatingVisible', {
      id: 'layer-window-map01',
      visible: true
    })

    // レイヤーウィンドウをオンボーディングツールチップの上に表示するためz-indexを高く設定
    vm.$nextTick(() => {
      const layerWindow = document.getElementById('layer-window-map01')
      if (layerWindow) {
        layerWindow.style.zIndex = '9999'
      }
    })
  })

  // DOM要素が見つかるまで待ってからツールチップを表示
  vm.$nextTick(() => {
    const waitForElement = () => {
      const step = getStep(purpose, 0)
      if (!step) return

      const element = document.querySelector(step.targetSelector)
      if (element) {
        // 要素が見つかったのでツールチップを表示
        vm.showOnboardingTooltip = true
      } else {
        // 要素がまだ見つからない場合は100ms後に再試行（最大5秒）
        const retryCount = vm._onboardingRetryCount || 0
        if (retryCount < 50) {
          vm._onboardingRetryCount = retryCount + 1
          setTimeout(waitForElement, 100)
        } else {
          console.warn('オンボーディング: 対象要素が見つかりませんでした', step.targetSelector)
          vm._onboardingRetryCount = 0
        }
      }
    }
    vm._onboardingRetryCount = 0
    waitForElement()
  }) */
}
// 次のステップへ
export function handleOnboardingNext(vm: VM) {
  if (vm.hasMoreSteps) {
    vm.onboardingStepIndex++
    // 一旦非表示にして、DOM更新後に再表示（位置を再計算させる）
    vm.showOnboardingTooltip = false
    vm.$nextTick(() => {
      // DOM要素が見つかるまで待ってからツールチップを表示
      const waitForElement = () => {
        const step = getStep(vm.onboardingPurpose, vm.onboardingStepIndex)
        if (!step) return

        const element = document.querySelector(step.targetSelector)
        if (element) {
          // 要素が見つかったのでツールチップを表示
          vm.showOnboardingTooltip = true
        } else {
          // 要素がまだ見つからない場合は100ms後に再試行（最大5秒）
          const retryCount = vm._onboardingRetryCount || 0
          if (retryCount < 50) {
            vm._onboardingRetryCount = retryCount + 1
            setTimeout(waitForElement, 100)
          } else {
            console.warn('オンボーディング: 対象要素が見つかりませんでした', step.targetSelector)
            vm._onboardingRetryCount = 0
          }
        }
      }
      vm._onboardingRetryCount = 0
      waitForElement()
    })
  } else {
    vm.handleOnboardingComplete()
  }
}
// オンボーディングをスキップ
export function handleOnboardingSkip(vm: VM) {
  vm.showPurposeSelector = false
  vm.showOnboardingTooltip = false
  vm.onboardingPurpose = null
  vm.onboardingStepIndex = 0
  vm.$store.commit('SKIP_ONBOARDING')

  try {
    localStorage.setItem('oh3_onboarding_completed', 'true')
  } catch (e) {
    console.warn('localStorage not available:', e)
  }
}
// オンボーディング完了
export function handleOnboardingComplete(vm: VM) {
  vm.showOnboardingTooltip = false
  vm.$store.commit('COMPLETE_ONBOARDING')

  try {
    localStorage.setItem('oh3_onboarding_completed', 'true')
  } catch (e) {
    console.warn('localStorage not available:', e)
  }
}